import { Tab } from '@headlessui/react';
import { HomeIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import AdminBreadcrumbBar from 'components/layout/admin-breadcrumb-bar';
import AdminNavGroupMenu from 'components/layout/admin-nav-group-menu';
import LoadingOverlay from 'components/layout/loading-overlay';
import Tooltip from 'components/layout/tooltip';
import { ADMIN_TAB_GROUPS } from 'data/admin-tabs';
import { fetchDefaultAdminData } from 'features/admin';
import { setActiveAdminTab } from 'features/ui';
import AirHandlersPage from 'pages/admin/air-handlers';
import AttributeFollowupsPage from 'pages/admin/attribute-followups';
import AttributesPage from 'pages/admin/attributes';
import BtuMultipliersPage from 'pages/admin/btu-multipliers';
import ChatbotCategoriesPage from 'pages/admin/chatbot-categories';
import ChatbotSubAgentToolsPage from 'pages/admin/chatbot-sub-agent-tools';
import ChatbotSubAgentsPage from 'pages/admin/chatbot-sub-agents';
import CoverageTooltipsPage from 'pages/admin/coverage-tooltips';
import CustomerPreferencesPage from 'pages/admin/customer-preferences';
import DesignRulesPage from 'pages/admin/design-rules';
import DuctedSystemFilteringPage from 'pages/admin/ducted-system-filtering';
import ErrorLogsPage from 'pages/admin/error-logs';
import ExistingEquipmentTooltipsPage from 'pages/admin/existing-equipment-tooltips';
import FilterTooltipsPage from 'pages/admin/filter-tooltips';
import FilteringPage from 'pages/admin/filtering';
import GlassPage from 'pages/admin/glass';
import InsulationLevelsPage from 'pages/admin/insulation-levels';
import OrchestratorModelsPage from 'pages/admin/orchestrator-models';
import OrchestratorTaskModelsPage from 'pages/admin/orchestrator-task-models';
import PromptsPage from 'pages/admin/prompts';
import ScoreDefinitionsPage from 'pages/admin/score-definitions';
import ScoringScenariosPage from 'pages/admin/scoring-scenarios';
import SpacesPage from 'pages/admin/spaces';
import StructureSubTypeRulesPage from 'pages/admin/structure-sub-type-rules';
import StructureTypesPage from 'pages/admin/structure-types';
import SystemSelectorCustomersPage from 'pages/admin/system-selector-customers';
import TextKeysPage from 'pages/admin/text-keys';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { enableHorizontalScroll } from 'utils/helpers';

export default function AdminPage() {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.admin);
  const activeTab = useAppSelector((state) => state.ui.activeAdminTab);

  const mainClasses = clsx(
    'w-full h-[100vh] bg-gray-100 select-none text-gray-900'
  );

  useEffect(() => {
    dispatch(fetchDefaultAdminData());
  }, [dispatch]);

  return (
    <main className={mainClasses}>
      <Tab.Group selectedIndex={activeTab}>
        <div className='flex h-full w-full flex-col'>
          <AdminBreadcrumbBar activeTabIndex={activeTab} />
          <div className='flex h-12 w-full shrink-0 bg-white'>
            <Tab.List className='sr-only'>
              {(() => {
                const allTabs = ADMIN_TAB_GROUPS.flatMap((g) => g.tabs);
                const maxIndex = Math.max(...allTabs.map((t) => t.index));
                return Array.from({ length: maxIndex + 1 }, (_, i) => {
                  const tab = allTabs.find((t) => t.index === i);
                  return (
                    <Tab key={tab?.name ?? `placeholder-${i}`}>
                      {tab?.name ?? ''}
                    </Tab>
                  );
                });
              })()}
            </Tab.List>
            <div
              className='hidden-scrollbar flex overflow-x-auto'
              onWheel={enableHorizontalScroll}
            >
              {ADMIN_TAB_GROUPS.map((group) => (
                <AdminNavGroupMenu
                  key={group.label}
                  group={group}
                  activeTabIndex={activeTab}
                  onSelectTab={(index) => dispatch(setActiveAdminTab(index))}
                />
              ))}
            </div>
            <div className='h-full grow border-b border-gray-200' />
            <a
              href={`/${window.location.search}`}
              className='relative flex h-full shrink-0 cursor-pointer items-center border-b border-l border-gray-200 px-3 text-gray-900 hover:bg-gray-50'
            >
              <HomeIcon className='h-6 w-6' />
              <Tooltip text='Back to System Selector' />
            </a>
          </div>
          <div className='flex grow'>
            <div className='relative grow'>
              <div className='absolute inset-0 overflow-y-auto p-5'>
                <DndProvider backend={HTML5Backend}>
                  <Tab.Panels className='focus:outline-none'>
                    <Tab.Panel className='focus:outline-none'>
                      <StructureTypesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <SpacesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <AttributesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <AttributeFollowupsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <GlassPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <AirHandlersPage />
                    </Tab.Panel>
                    {/* Reserved index 6 (legacy Config Ranking) */}
                    <Tab.Panel className='focus:outline-none' />
                    <Tab.Panel className='focus:outline-none'>
                      <FilteringPage />
                    </Tab.Panel>
                    {/* Reserved index 8 (legacy System Ranking) */}
                    <Tab.Panel className='focus:outline-none' />
                    <Tab.Panel className='focus:outline-none'>
                      <PromptsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <DesignRulesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <InsulationLevelsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <TextKeysPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <OrchestratorModelsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <OrchestratorTaskModelsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ChatbotCategoriesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ChatbotSubAgentsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ChatbotSubAgentToolsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <StructureSubTypeRulesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <BtuMultipliersPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ScoringScenariosPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ScoreDefinitionsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <CustomerPreferencesPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <SystemSelectorCustomersPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <DuctedSystemFilteringPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <FilterTooltipsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ExistingEquipmentTooltipsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <CoverageTooltipsPage />
                    </Tab.Panel>
                    <Tab.Panel className='focus:outline-none'>
                      <ErrorLogsPage />
                    </Tab.Panel>
                  </Tab.Panels>
                </DndProvider>
              </div>
            </div>
          </div>
        </div>
      </Tab.Group>
      <LoadingOverlay loading={loading} />
    </main>
  );
}
